import React, { useState } from 'react';
import { FileUpIcon, XIcon, AlertCircleIcon, CheckCircleIcon, TableIcon, FileIcon, DownloadIcon, HelpCircleIcon } from 'lucide-react';
import { ImportFormatPreview } from './ImportFormatPreview';
interface ImportJobsModalProps {
  isOpen: boolean;
  onClose: () => void;
}
export const ImportJobsModal = ({
  isOpen,
  onClose
}: ImportJobsModalProps) => {
  const [file, setFile] = useState<File | null>(null);
  const [showFormatPreview, setShowFormatPreview] = useState(false);
  const [uploadStep, setUploadStep] = useState<'upload' | 'validation' | 'success'>('upload');
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  if (!isOpen) return null;
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0] || null;
    setFile(selectedFile);
    if (selectedFile) {
      // In a real implementation, we would validate the Excel file here
      // For now, just simulate validation
      setTimeout(() => {
        setUploadStep('validation');
        // Simulate some validation errors for demonstration
        setValidationErrors(['Row 3: Missing Dump Location', "Row 5: Invalid Rate Type (must be 'Hourly' or 'Per Load')", 'Row 8: Invalid Date Format']);
      }, 1000);
    }
  };
  const handleConfirmImport = () => {
    // In a real implementation, we would process the file here
    // For now, just simulate success
    setUploadStep('success');
  };
  const handleDownloadTemplate = () => {
    // In a real implementation, this would download an Excel template
    alert('Template download would start here');
  };
  const handleClose = () => {
    setFile(null);
    setUploadStep('upload');
    setValidationErrors([]);
    setShowFormatPreview(false);
    onClose();
  };
  return <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-id="element-1861">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col" data-id="element-1862">
        {/* Header */}
        <div className="flex justify-between items-center border-b px-6 py-4" data-id="element-1863">
          <h2 className="text-xl font-bold" data-id="element-1864">Import Jobs</h2>
          <button onClick={handleClose} className="text-gray-500 hover:text-gray-700" data-id="element-1865">
            <XIcon size={20} data-id="element-1866" />
          </button>
        </div>
        {/* Content */}
        <div className="flex-1 overflow-auto p-6" data-id="element-1867">
          {showFormatPreview ? <div data-id="element-1868">
              <button className="mb-4 text-blue-600 hover:text-blue-800 flex items-center gap-1 text-sm" onClick={() => setShowFormatPreview(false)} data-id="element-1869">
                ← Back to Import
              </button>
              <ImportFormatPreview data-id="element-1870" />
            </div> : <>
              {uploadStep === 'upload' && <div data-id="element-1871">
                  <p className="mb-4 text-gray-600" data-id="element-1872">
                    Upload an Excel file with your job data. Make sure your file
                    follows the required format.
                  </p>
                  <div className="flex items-center gap-2 mb-6" data-id="element-1873">
                    <button className="text-blue-600 hover:text-blue-800 flex items-center gap-1 text-sm" onClick={() => setShowFormatPreview(true)} data-id="element-1874">
                      <TableIcon size={16} data-id="element-1875" />
                      View Import Format
                    </button>
                    <span className="text-gray-400" data-id="element-1876">|</span>
                    <button className="text-blue-600 hover:text-blue-800 flex items-center gap-1 text-sm" onClick={handleDownloadTemplate} data-id="element-1877">
                      <DownloadIcon size={16} data-id="element-1878" />
                      Download Template
                    </button>
                  </div>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center mb-6" data-id="element-1879">
                    {file ? <div className="flex flex-col items-center" data-id="element-1880">
                        <FileIcon size={40} className="text-green-500 mb-2" data-id="element-1881" />
                        <p className="font-medium mb-1" data-id="element-1882">{file.name}</p>
                        <p className="text-sm text-gray-500 mb-4" data-id="element-1883">
                          {(file.size / 1024).toFixed(2)} KB
                        </p>
                        <div className="flex gap-3" data-id="element-1884">
                          <button className="text-red-600 hover:text-red-800 text-sm" onClick={() => setFile(null)} data-id="element-1885">
                            Remove
                          </button>
                          <button className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm" data-id="element-1886">
                            Process File
                          </button>
                        </div>
                      </div> : <div className="flex flex-col items-center" data-id="element-1887">
                        <FileUpIcon size={40} className="text-gray-400 mb-2" data-id="element-1888" />
                        <p className="font-medium mb-2" data-id="element-1889">
                          Drag and drop your Excel file here
                        </p>
                        <p className="text-sm text-gray-500 mb-4" data-id="element-1890">or</p>
                        <label className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md cursor-pointer" data-id="element-1891">
                          Browse Files
                          <input type="file" className="hidden" accept=".xlsx,.xls" onChange={handleFileChange} data-id="element-1892" />
                        </label>
                      </div>}
                  </div>
                  <div className="bg-blue-50 border border-blue-200 rounded-md p-4 flex items-start gap-3" data-id="element-1893">
                    <HelpCircleIcon size={20} className="text-blue-500 mt-0.5 flex-shrink-0" data-id="element-1894" />
                    <div data-id="element-1895">
                      <div className="font-medium text-blue-800" data-id="element-1896">
                        Import Tips
                      </div>
                      <ul className="text-blue-700 text-sm list-disc pl-5 mt-1 space-y-1" data-id="element-1897">
                        <li data-id="element-1898">
                          Make sure your Excel file has the exact column headers
                          shown in the format preview
                        </li>
                        <li data-id="element-1899">Dates should be in MM/DD/YYYY format</li>
                        <li data-id="element-1900">Rate Type must be either "Hourly" or "Per Load"</li>
                        <li data-id="element-1901">Job Numbers must be unique</li>
                        <li data-id="element-1902">All required fields must be filled</li>
                      </ul>
                    </div>
                  </div>
                </div>}
              {uploadStep === 'validation' && <div data-id="element-1903">
                  <h3 className="text-lg font-medium mb-4" data-id="element-1904">
                    Validation Results
                  </h3>
                  {validationErrors.length > 0 ? <div className="mb-6" data-id="element-1905">
                      <div className="flex items-center gap-2 text-amber-600 mb-3" data-id="element-1906">
                        <AlertCircleIcon size={20} data-id="element-1907" />
                        <span className="font-medium" data-id="element-1908">
                          Your file has {validationErrors.length} issues that
                          need to be fixed
                        </span>
                      </div>
                      <div className="bg-amber-50 border border-amber-200 rounded-md p-4 mb-4" data-id="element-1909">
                        <ul className="list-disc pl-5 space-y-1" data-id="element-1910">
                          {validationErrors.map((error, index) => <li key={index} className="text-amber-700" data-id="element-1911">
                              {error}
                            </li>)}
                        </ul>
                      </div>
                      <p className="text-gray-600 text-sm mb-4" data-id="element-1912">
                        Please fix these issues in your Excel file and upload it
                        again.
                      </p>
                      <div className="flex gap-3" data-id="element-1913">
                        <button className="border border-gray-300 px-4 py-2 rounded-md hover:bg-gray-50" onClick={() => setUploadStep('upload')} data-id="element-1914">
                          Upload New File
                        </button>
                        <button className="bg-amber-600 hover:bg-amber-700 text-white px-4 py-2 rounded-md" onClick={handleConfirmImport} data-id="element-1915">
                          Import Anyway (Skip Errors)
                        </button>
                      </div>
                    </div> : <div className="mb-6" data-id="element-1916">
                      <div className="flex items-center gap-2 text-green-600 mb-3" data-id="element-1917">
                        <CheckCircleIcon size={20} data-id="element-1918" />
                        <span className="font-medium" data-id="element-1919">
                          Your file passed validation
                        </span>
                      </div>
                      <p className="text-gray-600 mb-4" data-id="element-1920">
                        Your file is ready to be imported. Click the button
                        below to continue.
                      </p>
                      <button className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md" onClick={handleConfirmImport} data-id="element-1921">
                        Confirm Import
                      </button>
                    </div>}
                </div>}
              {uploadStep === 'success' && <div className="text-center py-8" data-id="element-1922">
                  <div className="bg-green-100 rounded-full h-20 w-20 flex items-center justify-center mx-auto mb-4" data-id="element-1923">
                    <CheckCircleIcon size={40} className="text-green-600" data-id="element-1924" />
                  </div>
                  <h3 className="text-xl font-medium mb-2" data-id="element-1925">
                    Import Successful!
                  </h3>
                  <p className="text-gray-600 mb-6" data-id="element-1926">
                    Your jobs have been imported successfully. You can now view
                    them in your dashboard.
                  </p>
                  <button className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-md" onClick={handleClose} data-id="element-1927">
                    View Jobs
                  </button>
                </div>}
            </>}
        </div>
        {/* Footer */}
        {!showFormatPreview && uploadStep !== 'success' && <div className="border-t px-6 py-4 flex justify-end" data-id="element-1928">
            <button className="border border-gray-300 px-4 py-2 rounded-md hover:bg-gray-50 mr-3" onClick={handleClose} data-id="element-1929">
              Cancel
            </button>
            {uploadStep === 'upload' && file && <button className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md" onClick={() => setUploadStep('validation')} data-id="element-1930">
                Continue
              </button>}
          </div>}
      </div>
    </div>;
};